import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import i18n from '@/i18n';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** Catches render errors in a page so one broken view doesn't blank the whole app. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Page render error:', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="card mx-auto mt-8 max-w-lg text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger-50">
          <AlertTriangle className="h-6 w-6 text-danger-600" />
        </div>
        <h2 className="text-base font-semibold text-gray-900">{i18n.t('common.pageError')}</h2>
        <p className="mt-1 text-sm text-gray-500">{i18n.t('common.pageErrorHint')}</p>
        <button type="button" className="btn-primary mt-6" onClick={() => window.location.reload()}>
          <RotateCw className="h-4 w-4" />
          {i18n.t('common.reload')}
        </button>
      </div>
    );
  }
}
